import React, { useState, useRef, useEffect } from "react";
import { Message } from "../../types";
import "./Chat.css";

interface ChatProps {
  onSendMessage: (message: string) => void;
  onSpeak: () => void;
  onRecord: () => void;
  onMuteToggle: () => void;
  isRecording: boolean;
  vadListening: boolean;
  isMuted: boolean;
  loading: boolean;
  playing: boolean;
  hasTenantId: boolean;
  messages: Message[];
}

export const Chat: React.FC<ChatProps> = ({
  onSendMessage,
  onSpeak,
  onRecord,
  onMuteToggle,
  isRecording,
  vadListening,
  isMuted,
  loading,
  playing,
  hasTenantId,
  messages,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && hasTenantId) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  return (
    <div className={`chat-container ${isExpanded ? "expanded" : ""}`}>
      {/* Chat Header */}
      <div className="chat-header" onClick={toggleExpanded}>
        <div className="chat-title">
          <span>Chat</span>
          {messages.length > 0 && (
            <span className="message-count">{messages.length}</span>
          )}
        </div>
        {/* Header Action Buttons (stop propagation to avoid toggle) */}
        <div
          className="chat-actions"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          <button
            className={`circular-button speak-button ${
              loading ? "loading" : ""
            } ${playing ? "speaking" : ""}`}
            onClick={onSpeak}
            disabled={false}
            aria-label="Riproduci risposta"
          >
            {loading ? (
              <div className="loading-spinner" />
            ) : playing ? (
              <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="6" y="6" width="12" height="12" fill="#fff" />
              </svg>
            ) : (
              <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="6.75" r="4" fill="#fff" />
                <ellipse cx="10" cy="17.75" rx="7" ry="4" fill="#fff" />
              </svg>
            )}
          </button>
          <button
            className={`circular-button record-button ${
              vadListening ? "listening" : ""
            } ${isRecording ? "recording" : ""}`}
            onClick={onRecord}
            disabled={!hasTenantId}
            aria-label={vadListening ? "Ferma VAD" : "Avvia VAD"}
          >
            {vadListening ? (
              // Icona Stop quando VAD è attivo
              <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="6" y="6" width="12" height="12" rx="2" fill="#fff" />
              </svg>
            ) : (
              // Icona Microfono quando VAD non è attivo
              <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"
                  fill="#fff"
                />
                <path
                  d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"
                  fill="#fff"
                />
              </svg>
            )}
          </button>
          {/* Bottone Mute/Unmute - appare solo quando VAD è attivo */}
          {vadListening && (
            <button
              className={`circular-button mute-button ${isMuted ? "muted" : ""}`}
              onClick={onMuteToggle}
              aria-label={isMuted ? "Riattiva microfono" : "Muta microfono"}
            >
              {isMuted ? (
                // Icona Microfono barrato (Muted)
                <svg
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"
                    fill="#fff"
                  />
                </svg>
              ) : (
                // Icona Microfono attivo (Unmuted)
                <svg
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"
                    fill="#fff"
                  />
                  <path
                    d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"
                    fill="#fff"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
        <div className="chat-toggle">{isExpanded ? "−" : "+"}</div>
      </div>

      {/* Chat Content */}
      {isExpanded && (
        <div className="chat-content">
          {/* Messages */}
          <div className="messages-container">
            {messages.length === 0 ? (
              <div className="empty-state">
                <p>
                  Ciao! Sono il tuo assistente digitale. Puoi scrivermi un
                  messaggio o usare i pulsanti per interagire.
                </p>
              </div>
            ) : (
              messages.map((message) => {
                // Converti created_at in Date se è una string
                const messageDate =
                  typeof message.created_at === "string"
                    ? new Date(message.created_at)
                    : message.created_at;

                return (
                  <div
                    key={message.id}
                    className={`message ${
                      message.role === "user" ? "user" : "assistant"
                    }`}
                  >
                    <div className="message-content">{message.content}</div>
                    <div className="message-time">
                      {messageDate.toLocaleTimeString("it-IT", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="chat-input-form">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                hasTenantId ? "Scrivi un messaggio..." : "Tenant ID richiesto"
              }
              disabled={!hasTenantId}
              className="chat-input"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || !hasTenantId}
              className="send-button"
            >
              ➤
            </button>
          </form>

          {/* Action Buttons moved to header */}
        </div>
      )}
    </div>
  );
};
